// angleAudio.ts — Quel audio portent les angles de la multivue ?
//
// Par défaut, chaque angle porte l'audio de SA source (le micro de sa caméra). Quand le Switch audio
// est sur « Fixe », l'audio du programme EST la source fixe choisie, et la régie demande que TOUS
// les angles le portent — c'est le microservice qui l'applique, dans le HLS d'angle servi aux
// spectateurs (app/xtream_angle_hls_manager.py, message `angle_audio` du canal de contrôle).
//
// Les autres modes (Source, Mixeur) ne changent rien : chaque angle garde l'audio de sa source.

export type AngleAudioMode = 'program' | 'own';

/** La régie réaffirme son ordre à cette cadence ; le serveur le tient pour périmé au bout d'une minute. */
export const ANGLE_AUDIO_REASSERT_MS = 20_000;

export interface AngleAudioInputs {
  /** La chaîne est en multivue : sinon il n'y a pas d'angle publié, donc rien à demander. */
  multiviewActive    : boolean;
  switchMode         : 'source' | 'fixe' | 'mix';
  /**
   * La source fixe est réellement branchée sur le programme. Tant qu'elle ne l'est pas (slot retiré,
   * invité déconnecté, source hors deck pas encore acquise), la régie retombe sur la source active :
   * l'audio du programme n'est alors PAS la source fixe, et les angles ne doivent pas le prétendre.
   */
  fixedSourceResolved: boolean;
}

// Coupé le 2026-10-30 : le jumeau des angles de téléphones lit trop lentement (`reader is too slow`
// sur src_* : 0 du 20 au 28/10, 281 le 29, 860 le 30), l'angle est saccadé en multivue. Chaque angle
// garde l'audio de sa source. Ne repasser à true qu'une fois corrigé et validé sur un vrai direct.
const ANGLE_PROGRAM_AUDIO_ENABLED = false;

export const angleAudioMode = (i: AngleAudioInputs): AngleAudioMode =>
  ANGLE_PROGRAM_AUDIO_ENABLED && i.multiviewActive && i.switchMode === 'fixe' && i.fixedSourceResolved ? 'program' : 'own';

/**
 * `angles` : les angles de téléphones (identifiants natifs) pour lesquels le serveur doit fabriquer un jumeau
 * portant l'audio du programme. Vide ou absent hors du mode « programme ».
 */
export const angleAudioMessage = (mode: AngleAudioMode, angles: string[] = []): string =>
  JSON.stringify(mode === 'program' ? { type: 'angle_audio', mode, angles } : { type: 'angle_audio', mode });

// ── Les angles de téléphones (SFU) : un jumeau côté serveur ────────────────────
//
// Le path d'un téléphone (`src_{uuid}_{id}`) porte son micro et personne d'autre que lui n'y publie. Le
// microservice fabrique donc, en mode « programme », un jumeau `src_{uuid}_{id}-prog` (même vidéo, audio du
// programme) et dit à la régie, par `angle_audio_state`, lesquels sont PRÊTS. La régie déclare alors le
// jumeau à la place du path natif ; tant qu'il n'est pas prêt, ou dès qu'on quitte le mode, le path natif.

/** Doit rester identique à PROG_SUFFIX de ffmpeg-microservice/app/xtream_angle_hls_manager.py. */
export const ANGLE_PROG_SUFFIX = '-prog';

/** Identifiant natif d'un angle de téléphone, d'après son path ; null si ce n'en est pas un (ou déjà un jumeau). */
export const nativeAngleId = (path: string | undefined, channelUuid: string): string | null => {
  if (!path) return null;
  const prefix = `src_${channelUuid}_`;
  if (!path.startsWith(prefix)) return null;
  const id = path.slice(prefix.length);
  return /^[A-Za-z0-9_-]{1,80}$/.test(id) && !id.endsWith(ANGLE_PROG_SUFFIX) ? id : null;
};

/** Les identifiants natifs, triés et sans doublon : une liste stable, utilisable comme clé d'effet. */
export const nativeAngleIds = (slots: { mediamtxPath?: string }[], channelUuid: string): string[] =>
  Array.from(new Set(slots.map(s => nativeAngleId(s.mediamtxPath, channelUuid)).filter((id): id is string => !!id))).sort();

/** Le path à déclarer pour un angle : son jumeau s'il est prêt ET qu'on est en mode « programme », sinon tel quel. */
export const resolveAnglePath = (
  path: string, channelUuid: string, mode: AngleAudioMode, ready: ReadonlySet<string>,
): string => {
  if (mode !== 'program') return path;
  const id = nativeAngleId(path, channelUuid);
  return id && ready.has(id) ? `${path}${ANGLE_PROG_SUFFIX}` : path;
};

// ── Les angles que la RÉGIE publie elle-même (sources locales) ─────────────────
//
// Le message `angle_audio` ne suffit pas pour eux : leurs spectateurs lisent le chemin MediaMTX
// directement, sans passer par le HLS AAC du microservice. L'audio doit donc être posé AU
// MOMENT où la régie publie l'angle — c'est ce qui suit.

export interface AngleAudioChoice {
  /** Piste à publier à la place de l'audio propre de la source ; null = ne rien imposer. */
  forced       : MediaStreamTrack | null;
  /** Ce qui sera réellement publié : 'program' seulement si la piste du programme est vivante. */
  effectiveMode: AngleAudioMode;
}

/**
 * En mode « programme » on publie la sortie du programme (le bus que la régie envoie à l'antenne, donc la
 * source fixe avec ses réglages) sur chaque angle. Si cette piste n'existe pas ou n'est plus vivante, on
 * ne l'impose pas : l'angle garde l'audio de sa source plutôt que de partir muet.
 */
export const pickAngleAudio = (mode: AngleAudioMode, programTrack: MediaStreamTrack | null | undefined): AngleAudioChoice =>
  mode === 'program' && programTrack && programTrack.readyState === 'live'
    ? { forced: programTrack, effectiveMode: 'program' }
    : { forced: null, effectiveMode: 'own' };

/**
 * Un angle déjà publié doit être republié quand l'audio qu'il porte n'est plus celui voulu : on ne peut pas
 * lui ajouter une piste audio sans renégocier, et un angle publié sans audio n'a même pas de m-line audio.
 */
export const angleNeedsRepublish = (publishedMode: AngleAudioMode | undefined, wantedMode: AngleAudioMode): boolean =>
  publishedMode !== undefined && publishedMode !== wantedMode;
